import {
  NetworkCheck as NetworkCheckIcon,
  Refresh as RefreshIcon,
  WifiOff as WifiOffIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import {
  Alert,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import React from 'react';

function NetworkErrorHandler({ 
  error, 
  onRetry, 
  compact = false,
  showDetails = true, 
}) {
  const getErrorType = () => {
    if (!navigator.onLine) return 'offline';
    if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) return 'network';
    if (error?.response?.status >= 500) return 'server';
    if (error?.response?.status === 404) return 'notfound';
    if (error?.response?.status === 401 || error?.response?.status === 403) return 'auth';
    if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) return 'timeout';
    return 'unknown';
  };

  const getErrorInfo = () => {
    const type = getErrorType();
    
    switch (type) {
      case 'offline':
        return {
          icon: <WifiOffIcon />,
          title: '인터넷 연결 없음',
          message: '인터넷 연결을 확인하고 다시 시도해 주세요.',
          color: 'error',
          retryText: '다시 시도',
        };
      case 'network':
        return {
          icon: <NetworkCheckIcon />,
          title: '네트워크 오류',
          message: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
          color: 'error',
          retryText: '다시 연결',
        };
      case 'server':
        return {
          icon: <ErrorIcon />,
          title: '서버 오류',
          message: '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.',
          color: 'error',
          retryText: '다시 시도',
        };
      case 'timeout':
        return {
          icon: <NetworkCheckIcon />,
          title: '요청 시간 초과',
          message: '서버 응답 시간이 초과되었습니다. 네트워크 연결을 확인해 주세요.',
          color: 'warning',
          retryText: '다시 시도',
        };
      case 'notfound':
        return {
          icon: <ErrorIcon />,
          title: '리소스를 찾을 수 없음',
          message: '요청한 정보를 찾을 수 없습니다.',
          color: 'error',
          retryText: '새로고침',
        };
      case 'auth':
        return {
          icon: <ErrorIcon />,
          title: '인증 오류',
          message: '접근 권한이 없습니다. 다시 로그인해 주세요.',
          color: 'error',
          retryText: '새로고침',
        };
      default:
        return {
          icon: <ErrorIcon />,
          title: '알 수 없는 오류',
          message: '예상치 못한 오류가 발생했습니다.',
          color: 'error',
          retryText: '다시 시도',
        };
    }
  };

  const errorInfo = getErrorInfo();

  if (compact) {
    return (
      <Alert 
        severity={errorInfo.color}
        icon={errorInfo.icon}
        action={
          onRetry && (
            <Button color="inherit" size="small" onClick={onRetry}>
              {errorInfo.retryText}
            </Button>
          )
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {errorInfo.title}
        </Typography>
        <Typography variant="caption">
          {errorInfo.message}
        </Typography>
      </Alert>
    );
  }

  return (
    <Card sx={{ maxWidth: 500, mx: 'auto', my: 2 }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Box sx={{ color: `${errorInfo.color}.main`, mb: 2 }}>
          {React.cloneElement(errorInfo.icon, { sx: { fontSize: 48 } })}
        </Box>
        
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {errorInfo.title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {errorInfo.message}
        </Typography>

        {showDetails && error?.response?.status && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            오류 코드: {error.response.status} {error.response.statusText}
          </Typography>
        )}

        {onRetry && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            color={errorInfo.color === 'error' ? 'primary' : errorInfo.color}
          >
            {errorInfo.retryText}
          </Button>
        )}

        {process.env.NODE_ENV === 'development' && error && (
          <Box 
            sx={{ 
              mt: 2, 
              p: 1, 
              bgcolor: 'grey.100', 
              borderRadius: 1,
              textAlign: 'left',
            }}
          >
            <Typography variant="caption" component="div">
              Debug Info:
            </Typography>
            <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem' }}>
              {JSON.stringify(error, null, 2)}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default NetworkErrorHandler;